import { memo } from "react";
import { Mail, MapPin, Phone, Cookie } from "lucide-react";
import { CookieSettingsButton } from "@/components/CookieConsentBanner";

const LOGO_URL = "/george_energy_logo_optimized.png";
const OFFICE_ADDRESS = "Cabin 105, Covai Tech Park, Plot 12, Indira Nagar, Nehru Nagar West, Civil Aerodrome PO, Coimbatore – 641014";

const getMapUrl = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const query = encodeURIComponent(OFFICE_ADDRESS);
  return isIOS 
    ? `https://maps.apple.com/?q=${query}`
    : `https://www.google.com/maps/search/?api=1&query=${query}`;
};

// Social icons as inline SVGs for consistency
const SocialIcons = {
  Facebook: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  LinkedIn: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  Twitter: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  Instagram: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  )
};

export const Footer = memo(({ currentPage = 'home' }) => {
  const handleNavigation = (page, scrollTo = null) => {
    if (scrollTo) {
      window.dispatchEvent(new CustomEvent('navigate', { detail: { page, scrollTo } }));
    } else {
      window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
    }
  };

  const handleScrollToSection = (sectionId) => {
    // If on home page, scroll directly; otherwise navigate to home first
    if (currentPage === 'home') {
      const section = document.getElementById(sectionId);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    } else {
      handleNavigation('home', sectionId);
    }
  };

  const handleOurTeamClick = () => {
    // Navigate to About page and scroll to leadership section
    handleNavigation('about', 'leadership');
  };

  return (
    <footer 
      id="footer" 
      className="bg-lime-50 w-full"
      data-testid="site-footer"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Full-width top border */}
      <div className="border-t-4 border-lime-400 w-full" aria-hidden="true"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-10 lg:py-14">
        {/* Top Section - Logo & Tagline */}
        <div className="flex flex-col items-center mb-10">
          <img 
            src={LOGO_URL} 
            alt="George Energy Logo" 
            className="h-24 sm:h-28 lg:h-32 w-auto mb-3"
            style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }}
          />
          <p className="text-emerald-800 text-sm sm:text-base text-center max-w-md">
            Developing India&apos;s clean energy infrastructure
          </p>
        </div>
        
        {/* 3-Column Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 lg:gap-12">
          {/* Company */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-base sm:text-lg mb-4 text-emerald-950" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Company
            </h3>
            <ul className="space-y-2.5 text-emerald-800 text-sm sm:text-base text-center md:text-left">
              <li>
                <button 
                  onClick={() => handleNavigation('about')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-about-link"
                >
                  About
                </button>
              </li>
              <li>
                <button 
                  onClick={handleOurTeamClick} 
                  className="min-h-[24px] link-animate hover:text-lime-600 transition-colors"
                  data-testid="footer-team-link"
                >
                  Leadership
                </button>
              </li>
            </ul>
          </div>
          
          {/* Sectors */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-base sm:text-lg mb-4 text-emerald-950" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sectors
            </h3>
            <ul className="space-y-2.5 text-emerald-800 text-sm sm:text-base text-center md:text-left">
              <li>
                <button 
                  onClick={() => handleNavigation('solar')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-solar-link"
                >
                  Solar Infrastructure
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('wind')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-wind-link"
                >
                  Wind Infrastructure
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('public-infra')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-public-infra-link"
                >
                  Public Infrastructure
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('marine')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-marine-link"
                >
                  Marine Energy (R&amp;D)
                </button>
              </li>
            </ul>
          </div>
          
          {/* Resources */}
          <div className="flex flex-col items-center md:items-start col-span-2 md:col-span-1">
            <h3 className="font-semibold text-base sm:text-lg mb-4 text-emerald-950" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Resources
            </h3>
            <ul className="space-y-2.5 text-emerald-800 text-sm sm:text-base text-center md:text-left">
              <li>
                <button 
                  onClick={() => handleNavigation('insights')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-insights-link"
                >
                  Insights
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('accessibility')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-accessibility-link"
                >
                  Accessibility
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('contact')} 
                  className="min-h-[24px] hover:text-lime-600 transition-colors"
                  data-testid="footer-contact-link"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Social Links */}
        <div className="flex justify-center gap-4 sm:gap-5 mt-10 pt-8 border-t border-gray-400/50">
          <a 
            href="https://facebook.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 sm:w-11 sm:h-11 bg-white rounded-full flex items-center justify-center text-emerald-700 hover:text-lime-600 hover:bg-lime-50 transition-all shadow-sm"
            aria-label="Facebook"
          >
            <SocialIcons.Facebook />
          </a>
          <a 
            href="https://linkedin.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 sm:w-11 sm:h-11 bg-white rounded-full flex items-center justify-center text-emerald-700 hover:text-lime-600 hover:bg-lime-50 transition-all shadow-sm"
            aria-label="LinkedIn"
          >
            <SocialIcons.LinkedIn />
          </a>
          <a 
            href="https://twitter.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 sm:w-11 sm:h-11 bg-white rounded-full flex items-center justify-center text-emerald-700 hover:text-lime-600 hover:bg-lime-50 transition-all shadow-sm"
            aria-label="Twitter"
          >
            <SocialIcons.Twitter />
          </a>
          <a 
            href="https://instagram.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 sm:w-11 sm:h-11 bg-white rounded-full flex items-center justify-center text-emerald-700 hover:text-lime-600 hover:bg-lime-50 transition-all shadow-sm"
            aria-label="Instagram"
          >
            <SocialIcons.Instagram />
          </a>
        </div>
      </div>
      
      {/* Bottom Section */}
      <div className="border-t border-gray-400/50 w-full">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-4 sm:py-5">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between text-sm text-emerald-700">
            {/* Left: Copyright & CIN - all on one line */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="min-h-[24px] flex items-center">© {new Date().getFullYear()} George Energy. All Rights Reserved.</span>
              <span className="text-emerald-600">•</span>
              <span className="whitespace-nowrap min-h-[24px] flex items-center">
                CIN: <a 
                  href="https://www.tofler.in/george-energy-private-limited/company/U35109PN2025PTC241707"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-h-[24px] inline-flex items-center underline hover:text-lime-600 transition-colors ml-1"
                  data-testid="cin-link-desktop"
                >U35109PN2025PTC241707</a>
              </span>
            </div>
            
            {/* Right: Links */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button 
                onClick={() => handleNavigation('privacy')}
                className="min-h-[24px] hover:text-lime-600 transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </button>
              <span className="text-emerald-600">•</span>
              <button 
                onClick={() => handleNavigation('terms')}
                className="min-h-[24px] hover:text-lime-600 transition-colors"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </button>
              <span className="text-emerald-600">•</span>
              <CookieSettingsButton />
              <span className="text-emerald-600">•</span>
              <span className="flex items-center gap-1 whitespace-nowrap min-h-[24px]">
                Made in <span className="text-lg">🇮🇳</span>
              </span>
            </div>
          </div>
          
          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col items-center gap-3 text-sm text-emerald-700">
            {/* Row 1: Links */}
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <button 
                onClick={() => handleNavigation('privacy')}
                className="min-h-[24px] hover:text-lime-600 transition-colors"
                data-testid="footer-privacy-link-mobile"
              >
                Privacy Policy
              </button>
              <span className="text-emerald-600">•</span>
              <button 
                onClick={() => handleNavigation('terms')}
                className="min-h-[24px] hover:text-lime-600 transition-colors"
                data-testid="footer-terms-link-mobile"
              >
                Terms
              </button>
              <span className="text-emerald-600">•</span>
              <CookieSettingsButton />
            </div>
            
            {/* Row 2: Copyright */}
            <div className="text-center min-h-[24px] flex items-center">
              <span>© {new Date().getFullYear()} George Energy. All Rights Reserved.</span>
            </div>
            
            {/* Row 3: Made in India & CIN - ALL ON ONE LINE */}
            <div className="flex items-center gap-2 text-xs text-emerald-800 whitespace-nowrap min-h-[24px]">
              <span className="flex items-center gap-1">
                Made in <span className="text-base">🇮🇳</span>
              </span>
              <span>•</span>
              <span>CIN: <a 
                href="https://www.tofler.in/george-energy-private-limited/company/U35109PN2025PTC241707"
                target="_blank"
                rel="noopener noreferrer"
                className="min-h-[24px] inline-flex items-center underline hover:text-lime-600 transition-colors"
                data-testid="cin-link-mobile"
              >U35109PN2025PTC241707</a></span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});
